import { Col, Form, Select } from "antd";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import { getData } from "../../../apiServices/common";

const ImportVehicleAssignmentFilterFields = ({ initialTableData }) => {
  // State for options
  const [lcOptions, setLcOptions] = useState([]);
  const [salesContractOptions, setSalesContractOptions] = useState([]);
  const [invoiceOptions, setInvoiceOptions] = useState([]);

  // Loading states
  const [loadingLc, setLoadingLc] = useState(false);
  const [loadingSalesContract, setLoadingSalesContract] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  // Type of Shipment options (static)
  const shipmentTypeOptions = [
    { label: "LC", value: "LC" },
    { label: "FOC", value: "FOC" },
    { label: "Commercial", value: "Commercial" },
    { label: "Sample", value: "Sample" },
    { label: "Others", value: "Others" },
  ];

  // Fetch LC options
  const fetchLcOptions = async (searchValue = "") => {
    setLoadingLc(true);
    try {
      const response = await getData(
        "/api/accounting/get-all-b2blcs-terms",
        null,
        {
          search: searchValue,
          per_page: 10,
        },
      );

      if (response?.data?.code === 200) {
        // Handle both possible response structures
        const dataArray = response.data.data?.data || response.data.data || [];
        const options = dataArray.map((item) => ({
          label: item.b2b_lc_number,
          value: item.id,
        }));
        setLcOptions(options);
      }
    } catch (error) {
      console.error("Error fetching LC options:", error);
      setLcOptions([]);
    } finally {
      setLoadingLc(false);
    }
  };

  // Fetch Sales Contract options
  const fetchSalesContractOptions = async (searchValue = "") => {
    setLoadingSalesContract(true);
    try {
      const response = await getData("/api/sales_contract", null, {
        reference_no: searchValue,
        per_page: 10,
      });

      if (response?.data?.code === 200) {
        // Handle both possible response structures
        const dataArray = response.data.data?.data || response.data.data || [];
        const options = dataArray.map((item) => ({
          label: item.reference_no,
          value: item.id,
        }));
        setSalesContractOptions(options);
      }
    } catch (error) {
      console.error("Error fetching Sales Contract options:", error);
      setSalesContractOptions([]);
    } finally {
      setLoadingSalesContract(false);
    }
  };

  // Fetch Invoice options
  const fetchInvoiceOptions = async (searchValue = "") => {
    setLoadingInvoice(true);
    try {
      const response = await getData("/api/accounting/import-details", null, {
        invoice_number: searchValue,
        per_page: 10,
      });

      if (response?.data?.code === 200) {
        // Handle both possible response structures
        const dataArray = response.data.data?.data || response.data.data || [];
        const options = dataArray.map((item) => ({
          label: item.invoice_number,
          value: item.id,
        }));
        setInvoiceOptions(options);
      }
    } catch (error) {
      console.error("Error fetching Invoice options:", error);
      setInvoiceOptions([]);
    } finally {
      setLoadingInvoice(false);
    }
  };

  // Debounced search functions
  const debouncedFetchLcOptions = debounce((value) => {
    fetchLcOptions(value);
  }, 300);

  const debouncedFetchSalesContractOptions = debounce((value) => {
    fetchSalesContractOptions(value);
  }, 300);

  const debouncedFetchInvoiceOptions = debounce((value) => {
    fetchInvoiceOptions(value);
  }, 300);

  // Load initial options on component mount
  useEffect(() => {
    fetchLcOptions();
    fetchSalesContractOptions();
    fetchInvoiceOptions();
  }, []);

  return (
    <>
      <Col span={6}>
        <Form.Item label="Type of Shipment" name="type_of_shipment">
          <Select
            placeholder="Select Type of Shipment"
            options={shipmentTypeOptions}
            allowClear
            size="small"
          />
        </Form.Item>
      </Col>

      <Col span={6}>
        <Form.Item label="LC Number" name="b2b_lc_id">
          <Select
            showSearch
            placeholder="Search LC Number"
            options={lcOptions}
            loading={loadingLc}
            onSearch={debouncedFetchLcOptions}
            filterOption={false}
            allowClear
            size="small"
          />
        </Form.Item>
      </Col>

      <Col span={6}>
        <Form.Item label="Sales Contract" name="sales_contract_id">
          <Select
            showSearch
            placeholder="Search Sales Contract"
            options={salesContractOptions}
            loading={loadingSalesContract}
            onSearch={debouncedFetchSalesContractOptions}
            filterOption={false}
            allowClear
            size="small"
          />
        </Form.Item>
      </Col>

      <Col span={6}>
        <Form.Item label="Invoice" name="import_details_id">
          <Select
            showSearch
            placeholder="Search Invoice Number"
            options={invoiceOptions}
            loading={loadingInvoice}
            onSearch={debouncedFetchInvoiceOptions}
            filterOption={false}
            allowClear
            size="small"
          />
        </Form.Item>
      </Col>
    </>
  );
};

export default ImportVehicleAssignmentFilterFields;
